import { store, store$ } from '@redux/store';
import { 
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  fromEvent,
  map,
  skip,
  withLatestFrom
} from 'rxjs';
import {
  selectIsAuthModalOpen,
  selectIsLoggedIn,
  selectUserId,
  selectEmail,
  selectName
} from 'features/userProfile';
import { 
  extendedFacetsSelector,
  selectAppliedFacets,
  selectListingCount,
  selectResultType,
  selectSearchParameters
} from 'features/searchResultsFacets';
import modalService from 'layout/services/modalService';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { selectModal } from 'layout/redux/selectors';
import { selectCurrentUrl } from 'features/routing';
import onOpenModal from '../../userProfile/components/modal';
import { saveSearch$, getSavedSearchName } from '../services/saveSearchService';
import CreatedSaveSearchModal from './createdSaveSearchModal';

/**
 * Configuration object for the saved search functionality.
 * 
 * @property {string} savedSearchStorageKey - The key used to store saved search data in sessionStorage.
 * @property {HTMLElement} saveSearchButton - The DOM element for the "Save Search" button.
 * @property {boolean} areTherePendingSavedSearches - A flag indicating if there are pending saved searches.
 * @property {Observable} saveSearchSubscription - The current subscription for saving searches.
 */
const configuration = {
  savedSearchStorageKey: 'areTherePendingSavedSearches',
  saveSearchButton: document.querySelector(`[data-saved-search]`),
  areTherePendingSavedSearches: false,
  saveSearchSubscription: null,
  createSavedSearchModal: null,
  modalId: 'createdSaveSearch',
};

/**
 * Displays the "Created Save Search" modal.
 * Initializes a new instance of the `CreatedSaveSearchModal` class, updates the modal state in the Redux store,
 * and sets the modal's HTML content using the modal service.
 */
const showCreatedSaveSearchModal = (savedSearchInformation) => {
  configuration.createSavedSearchModal = new CreatedSaveSearchModal();
  configuration.createSavedSearchModal.savedSearchName = getSavedSearchName(savedSearchInformation);
  configuration.createSavedSearchModal.id = configuration.modalId;

  modalService.onHtmlRemoved({
    name: configuration.modalId,
    fn: configuration.createSavedSearchModal.unbindEvents.bind(configuration.createSavedSearchModal),
  });

  modalService.onHtmlReady({
    name: configuration.modalId,
    fn: configuration.createSavedSearchModal.bindEvents.bind(configuration.createSavedSearchModal),
  });

  store.dispatch(uiStateSlice.actions.modalInformation({
    name: configuration.modalId,
    variation: configuration.createSavedSearchModal.savedSearchName
  }));
  
  modalService.setHtml({ payload: configuration.createSavedSearchModal.getHtml() });
};

/**
 * Handles the closure of the "Created Save Search" modal.
 * Resets the `createSavedSearchModal` configuration to `null`,
 * clears the modal state in the Redux store, and removes the modal HTML from the DOM.
 */
const onCreateSavedSearchModalClosed = () => {
  modalService.clearHtml();
};

/**
 * Toggles the visibility of the "Save Search" button based on the listing count.
 *
 * @param {number} listingCount - The number of listings. If 0, the button will be hidden;
 * otherwise, it will be visible.
 */
const toggleSavedSearchButtonVisibility = (listingCount) => {
  configuration.saveSearchButton.toggleAttribute('hidden', listingCount === 0);
}

/**
 * Saves the current search to the user's saved searches.
 * Constructs the search parameters and triggers the save search subscription.
 */
const saveSearch = () => {
  const currentState = store.getState();
  const currentUrl = selectCurrentUrl(currentState);
  const filtersApplied = selectAppliedFacets(currentState);
  const resultType = selectResultType(currentState);
  const currentFacets = extendedFacetsSelector(currentState);
  const userId = selectUserId(currentState);
  const userEmail = selectEmail(currentState);
  const userName = selectName(currentState);
  const searchParameters = selectSearchParameters(currentState);

  const savedSearchInformation = {
    firstFilterApplied: filtersApplied[0],
    currentFacets,
    resultType,
    userId,
    userEmail,
    userName,
    currentUrl,
    searchParameters,
  };

  if (configuration.saveSearchSubscription) {
    configuration.saveSearchSubscription.unsubscribe();
  }

  const saveSearchObservable = saveSearch$(savedSearchInformation);
  configuration.saveSearchSubscription = saveSearchObservable.subscribe();

  showCreatedSaveSearchModal(savedSearchInformation);
};

/**
 * Sets a flag in sessionStorage indicating that a saved search is pending.
 */
const setPendingSavedSearchFlag = () => {
  sessionStorage.setItem(configuration.savedSearchStorageKey, true);
};

/**
 * Clears the flag in sessionStorage indicating that a saved search is pending.
 */
const clearPendingSavedSearchFlag = () => {
  sessionStorage.setItem(configuration.savedSearchStorageKey, false);
};

/**
 * Handles the "Save Search" button click event.
 * If the user is not logged in, it sets a pending saved search flag and opens the login modal.
 * If the user is logged in, it triggers the save search action.
 *
 * @param {Object} param - The event and user authentication state.
 * @param {Event} param.event - The click event.
 * @param {boolean} param.isLoggedIn - Whether the user is logged in.
 */
const onSaveSearchClicked = ({ event, isLoggedIn }) => {
  if (!isLoggedIn) {
    configuration.areTherePendingSavedSearches = true;
    onOpenModal(event);
    return;
  }

  saveSearch();
};

/**
 * Checks if there is a pending saved search in sessionStorage.
 * If a pending search exists, it triggers the save search action.
 */
const onPendingSaveSearches = () => {
  const isSearchAwaitingSave = JSON.parse(sessionStorage.getItem(configuration.savedSearchStorageKey));
  if (!isSearchAwaitingSave) {
    return;
  }

  clearPendingSavedSearchFlag();
  saveSearch();
};

/**
 * Observable that emits when the user's authentication state changes.
 * Skips the initial state and emits only subsequent changes.
 */
const onUserAuthenticationChanged$ = store$.pipe(
  map(selectIsLoggedIn),
  distinctUntilChanged(),
  skip(1)
);

/**
 * Observable that listens for "Save Search" button clicks and combines it with the user's authentication state.
 */
const onSaveSearchClicked$ = fromEvent(configuration.saveSearchButton, 'click').pipe(
  withLatestFrom(onUserAuthenticationChanged$),
  map(([event, isLoggedIn]) => ({ event, isLoggedIn }))
);

/**
 * Observable that emits when the authentication modal state changes.
 * Skips the initial state and emits only subsequent changes.
 */
const onSignInModalChanged$ = store$.pipe(
  map(selectIsAuthModalOpen),
  distinctUntilChanged(),
  skip(1)
);

/**
 * Observable that emits when the authentication modal is closed.
 */
const onSignInModalClosed$ = onSignInModalChanged$.pipe(
  filter((isAuthModalOpen) => !isAuthModalOpen)
);

/**
 * Observable that emits when the user is authenticated and there are pending saved searches.
 * Filters the authentication state to only emit when the user is logged in and there are pending saved searches.
 */
const onAuthenticatedUserFromSavedSearch$ = fromEvent(document.body, "loginSuccess").pipe(
  filter(() => configuration.areTherePendingSavedSearches)
);

/**
 * Observable that emits when a user is identified (i.e., a valid user ID is detected).
 * Filters the user ID to only emit when it changes and is truthy.
 */
const onIdentifiedUser$ = store$.pipe(
  map(selectUserId),
  distinctUntilChanged(),
  filter(Boolean)
);

/**
 * Observable that listens for click events on the document body.
 * Filters the events to detect clicks on the modal close button or the overlay element.
 *
 * @type {Observable}
 * Emits the click event when the target is either the modal close button or the overlay element.
 */
const bodyClicks$ = fromEvent(document.body, 'click').pipe(
  distinctUntilChanged(),
);

/**
 * Observable that listens for changes in the modal state.
 * Emits the modal state whenever the modal name changes.
 *
 * @type {Observable}
 * Emits the modal state object when the modal name changes.
 */
const onModalChanged$ = store$.pipe(
  map(selectModal),
  filter(Boolean),
  distinctUntilKeyChanged('name')
);

/**
 * Observable that listens for changes in the modal state and body click events.
 * Combines the latest modal state and body click events to detect when the "Created Save Search" modal
 * should be closed.
 *
 * @type {Observable}
 * Emits an array containing the modal state and the body click event when the modal name is 'createdSaveSearch'.
 */
const onCreateSavedSearchModalClosed$ = combineLatest(onModalChanged$, bodyClicks$).pipe(
  filter(([modal]) => modal?.name === configuration.modalId),
  filter(([, event]) => {
    const target = event.target;
    const closeButton = target.closest('[data-modal-close-save-search]');
    const overlayElement = target.closest('#overlayElement');

    return (!!closeButton && document.contains(closeButton)) ||
      (!!overlayElement && document.contains(overlayElement));
  }),
);

/**
 * Observable that emits the listing count whenever it changes.
 * It listens to the store, selects the listing count, and emits
 * only distinct values to avoid unnecessary updates.
 *
 * @constant {Observable<number>} onListingCountsChanged$
 */
const onListingCountsChanged$ = store$.pipe(
  map(selectListingCount),
  distinctUntilChanged());

// Subscribes to the "Save Search" button click observable.
onSaveSearchClicked$.subscribe(onSaveSearchClicked);

// Subscribes to the authentication modal closed observable to clear the pending saved search flag.
onSignInModalClosed$.subscribe(() => { configuration.areTherePendingSavedSearches = false; });

// Subscribes to the observable that emits when an authenticated user is detected after a pending saved search.
// When triggered, it sets a flag in sessionStorage indicating that a saved search is pending.
onAuthenticatedUserFromSavedSearch$.subscribe(setPendingSavedSearchFlag);

/**
 * Subscribes to the `onIdentifiedUser$` observable.
 * When a valid user ID is detected, it triggers the `onPendingSaveSearches` function
 * to check and handle any pending saved searches.
 */
onIdentifiedUser$.subscribe(onPendingSaveSearches);

/**
 * Subscribes to the `onCreateSavedSearchModalClosed$` observable.
 * This observable listens for events where the "Created Save Search" modal is closed.
 * It combines modal state changes and body click events to detect when the modal should be closed.
 * When triggered, it executes the `onCreateSavedSearchModalClosed` function to clean up the modal state.
 */
onCreateSavedSearchModalClosed$.subscribe(onCreateSavedSearchModalClosed);

/**
 * Subscribes to the `onListingCountsChanged$` observable.
 * This observable listens for changes in the listing count from the Redux store.
 * When the listing count changes, it triggers the `toggleSavedSearchButtonVisibility` function
 * to update the visibility of the "Save Search" button based on the current listing count.
 */
onListingCountsChanged$.subscribe(toggleSavedSearchButtonVisibility);